import React, { useEffect, useState } from "react";
import { withAuthenticationRequired } from "../context/AuthContext";
import { useDispatch } from "react-redux";
import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from "@mui/material";
import Loading from "../components/Loading";
import { HallazgosValidacionList } from "../components/HallazgosValidacionList";
import { TransicionEstadoDialog } from "../components/TransicionEstadoDialog";
import { showSuccessMessage } from "../redux/appSlice";
import { useRequestHandler } from "../utils/useRequestHandler";
import Servicios from "../services";

/**
 * Pantalla nueva "Habilitar temporada nueva" (ver REINGENIERIA_BACKOFFICE.md,
 * sección "Diseño UX del Backoffice"). No es una acción sobre una fila de
 * Concursos: cambia un parámetro global del sistema (CONCURSO_ACTUAL), por
 * eso tiene entrada propia en el menú en vez de un ícono más en la grilla.
 *
 * Flujo: elegir un concurso candidato (Activo + PrimeraRueda + no-actual, ya
 * filtrado server-side) → traer la vista previa de validación → si el
 * concurso actual vigente no está resuelto (no llegó a "Resultados
 * disponibles"), la pantalla queda bloqueada de entrada con el motivo
 * explícito y no se muestra nada más; si está resuelto, se muestran siempre
 * visibles los hallazgos de la validación completa de temporada del
 * candidato, y el botón de activar queda habilitado solo con cero errores.
 */
export const HabilitarTemporada = () => {
  const sendRequest = useRequestHandler();
  const dispatch = useDispatch();

  const [candidatos, setCandidatos] = useState([]);
  const [candidatoId, setCandidatoId] = useState("");
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);

  const cargarCandidatos = () => {
    sendRequest(Servicios.ObtenerCandidatosTemporada, [], data => {
      setCandidatos(data?.data ?? []);
    });
  };

  useEffect(() => {
    cargarCandidatos();
  }, []);

  useEffect(() => {
    if (!candidatoId) {
      setVistaPrevia(null);
      return;
    }
    sendRequest(
      Servicios.ObtenerVistaPreviaHabilitarTemporada,
      [candidatoId],
      data => {
        setVistaPrevia(data?.data);
      }
    );
  }, [candidatoId]);

  const handleConfirmarActivacion = async () => {
    await Servicios.HabilitarComoActual(candidatoId);
    dispatch(
      showSuccessMessage(
        `"${vistaPrevia?.nombre}" ahora es la temporada actual del sistema.`
      )
    );
    setCandidatoId("");
    setVistaPrevia(null);
    cargarCandidatos();
  };

  // M1-07: si falta la foto de la edición saliente, se cierra desde acá y se
  // vuelve a pedir la vista previa.
  const handleCerrarEdicion = () => {
    sendRequest(
      Servicios.CerrarEdicion,
      [vistaPrevia.edicionPorCerrarConcursoId],
      data => {
        dispatch(
          showSuccessMessage(
            `Edición ${vistaPrevia.edicionPorCerrarAnio} cerrada: ${data?.data ?? 0} conjuntos guardados en el histórico.`
          )
        );
        sendRequest(
          Servicios.ObtenerVistaPreviaHabilitarTemporada,
          [candidatoId],
          previa => setVistaPrevia(previa?.data)
        );
      }
    );
  };

  const candidatoSeleccionado = candidatos.find(c => c.id === candidatoId);

  return (
    <div className="card-modern">
      <div className="page-header">
        <h4 className="page-title">Habilitar temporada nueva</h4>
      </div>
      <hr className="border-gray-200 dark:border-gray-700 my-4" />

      <div className="mb-3" style={{ maxWidth: 420 }}>
        <FormControl fullWidth size="small">
          {/* `shrink`: sin esto el label no flota (nada lo fuerza a hacerlo
              con un Select vacío) y queda superpuesto con el texto del
              MenuItem vacío de abajo ("Seleccioná un concurso candidato") —
              ambos ocupan la misma posición vertical. Gotcha conocido de MUI
              al combinar `Select displayEmpty` con `InputLabel`. */}
          <InputLabel id="habilitar-temporada-candidato-label" shrink>
            Concurso candidato
          </InputLabel>
          <Select
            labelId="habilitar-temporada-candidato-label"
            label="Concurso candidato"
            value={candidatoId}
            displayEmpty
            inputProps={{ "aria-label": "Concurso candidato" }}
            onChange={event => setCandidatoId(event.target.value)}
          >
            <MenuItem value="">
              <em>Seleccioná un concurso candidato</em>
            </MenuItem>
            {candidatos.map(c => (
              <MenuItem key={c.id} value={c.id}>
                {c.nombre} ({c.anio})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {candidatos.length === 0 && (
          <Typography variant="body2" className="mt-2">
            No hay concursos candidatos disponibles (deben estar activos, en
            Primera rueda, y no ser el concurso actual).
          </Typography>
        )}
      </div>

      {vistaPrevia && !vistaPrevia.concursoActualResuelto && (
        <Alert severity="warning" className="my-4">
          {vistaPrevia.motivoBloqueo}
          {vistaPrevia.edicionPorCerrarConcursoId && (
            <div className="mt-2">
              <Button variant="outlined" size="small" onClick={handleCerrarEdicion}>
                Cerrar edición {vistaPrevia.edicionPorCerrarAnio}
              </Button>
            </div>
          )}
        </Alert>
      )}

      {vistaPrevia && vistaPrevia.concursoActualResuelto && (
        <>
          <hr className="border-gray-200 dark:border-gray-700 my-4" />
          <Typography variant="subtitle1" className="mb-2">
            Validación de temporada — {vistaPrevia.nombre}
            {vistaPrevia.anio ? ` (${vistaPrevia.anio})` : ""}
          </Typography>
          <HallazgosValidacionList
            hallazgos={vistaPrevia.hallazgos}
            cantidadErrores={vistaPrevia.cantidadErrores}
            cantidadAdvertencias={vistaPrevia.cantidadAdvertencias}
            mensajeVacio="La temporada está lista: no se encontraron problemas."
          />
          <div className="mt-4">
            <Button
              variant="contained"
              disabled={!vistaPrevia.puedeActivar}
              onClick={() => setDialogoAbierto(true)}
            >
              Activar esta temporada
            </Button>
          </div>
        </>
      )}

      <TransicionEstadoDialog
        open={dialogoAbierto}
        onClose={() => setDialogoAbierto(false)}
        titulo={`Activar "${vistaPrevia?.nombre ?? candidatoSeleccionado?.nombre ?? ""}" como temporada actual`}
        acciones={[
          "Se activa este concurso como la temporada actual del sistema.",
          "Los usuarios empiezan a ver esta temporada como la vigente en la app.",
          // M1-12: el aviso "tu penca vuelve" sale en este mismo paso.
          "Se avisa por push a todas las cuentas que volvió la penca (una vez por usuario y temporada; respeta sus preferencias)."
        ]}
        hallazgos={vistaPrevia?.hallazgos}
        cantidadErrores={vistaPrevia?.cantidadErrores}
        cantidadAdvertencias={vistaPrevia?.cantidadAdvertencias}
        puedeConfirmar={!!vistaPrevia?.puedeActivar}
        mensajeExito="Listo. La temporada quedó activada como la actual del sistema."
        onConfirmar={handleConfirmarActivacion}
      />
    </div>
  );
};

export default withAuthenticationRequired(HabilitarTemporada, {
  onRedirecting: () => <Loading />
});
